import { MihatoMark } from "@/common/components/MihatoMark";
import { type ReactNode, useEffect } from "react";
import { Link } from "react-router-dom";

type Props = {
  title: string;
  lastUpdated: string;
  children: ReactNode;
};

export function LegalLayout({ title, lastUpdated, children }: Props) {
  // React Router no resetea el scroll al navegar entre rutas. Cuando entrás
  // desde el footer del landing (que requiere scroll-down), aterrizás en el
  // legal con scroll heredado — forzamos el reset al tope.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <div
        className="landing-texture pointer-events-none fixed inset-0 opacity-50"
        aria-hidden="true"
      />

      <main className="relative min-h-screen bg-brand-dark text-white">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-[60vh]"
          style={{
            background:
              "radial-gradient(circle at 50% 10%, rgba(0, 229, 153, 0.06) 0%, transparent 60%)",
          }}
        />

        <div className="relative mx-auto flex min-h-screen w-full max-w-3xl flex-col px-6 py-10 md:px-8 md:py-16">
          <header className="mb-10 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3" aria-label="Volver al inicio de HATO">
              <MihatoMark className="h-7 w-7 text-white" />
              <span className="font-medium text-lg tracking-tight">HATO</span>
            </Link>
            <Link to="/" className="text-neutral-400 text-sm transition-colors hover:text-white">
              ← Volver
            </Link>
          </header>

          <div className="mb-10 flex flex-col gap-2 border-white/[0.06] border-b pb-8">
            <h1 className="font-medium text-3xl text-white tracking-tight md:text-4xl">{title}</h1>
            <p className="text-neutral-500 text-sm">Última actualización: {lastUpdated}</p>
          </div>

          <article className="legal-prose flex flex-col gap-8 text-[16px] text-neutral-300 leading-relaxed">
            {children}
          </article>

          <footer className="mt-16 border-white/[0.06] border-t pt-8 text-neutral-500 text-sm">
            <p>
              Última actualización: {lastUpdated} · Contacto:{" "}
              <a
                href="mailto:info@mihato.co"
                className="text-neutral-300 transition-colors hover:text-white"
              >
                info@mihato.co
              </a>
            </p>
          </footer>
        </div>
      </main>
    </>
  );
}

type SectionProps = {
  number: number | string;
  title: string;
  children: ReactNode;
};

export function LegalSection({ number, title, children }: SectionProps) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="font-medium text-white text-xl tracking-tight md:text-2xl">
        <span className="mr-3 text-neutral-500">{number}.</span>
        {title}
      </h2>
      <div className="flex flex-col gap-4 text-neutral-300">{children}</div>
    </section>
  );
}
